import React, { useEffect, useState } from 'react';
import {
  ArrowLeft,
  Building2,
  Eye,
  EyeOff,
  Globe2,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { CommanServices } from '../services/commanServices.jsx';
import { connectSocket, disconnectSocket } from '../Redux/Action/socketActions';
import { logoutModalToggle } from '../Redux/Action/organizationActions';
import ForgotPasswordModal from './ForgotPasswordModal';
import {
  MOBILE_APP_ONLY_MESSAGE,
  clearWebSession,
  hasWebAccess,
} from '../auth/webAccess';
import {
  apiErrorMessage,
  extractAuthenticationResult,
  persistAuthenticationResult,
} from '../auth/saasSession';
import { SaaSServices } from '../services/saasServices';
import { loginDestinationForUser } from './loginDestination';
import { persistAuthLanguage, readAuthLanguage } from '../auth/authLanguage';
import { finishLogoutTransition } from '../auth/logoutTransition';
import { resetMarketplaceGuideForNextLogin } from '../auth/marketplaceGuideSession';
import brandMark from '../assets/brand-assets/buoy-mark.svg';
import brandWatermark from '../assets/brand-assets/buoy-watermark-white.png';
import './Login.css';

const PAGE_COPY = {
  en: {
    dir: 'ltr',
    languageButton: 'عربي',
    languageButtonLabel: 'Switch to Arabic',
    brandLines: ['Disaster', 'Management', 'Smart System'],
    headline: ['Ready before it happens.', 'In control when it does.'],
    phases: [
      { key: 'prevention', color: '#28ADE3', text: 'Prevention — see risk before it forms' },
      { key: 'preparedness', color: '#FFC000', text: 'Preparedness — plans, people, readiness' },
      { key: 'response', color: '#EF3937', text: 'Response — coordinate in real time' },
      { key: 'recovery', color: '#3CB371', text: 'Recovery — restore and learn' },
    ],
    trust: [
      { value: '24/7', label: 'COMMAND CENTER' },
      { value: '< 60s', label: 'ALERT TO DISPATCH' },
      { value: 'Real-time', label: 'INTEGRATED RESPONSE' },
    ],
    loginTitle: 'Login to DisasterMS',
    noAccount: "Don't have an account?",
    register: 'Register',
    emailLabel: 'Email',
    emailPlaceholder: 'Enter your email address',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Enter your password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    accessConsole: 'Access console',
    signingIn: 'Signing in…',
    forgotPassword: 'Forgot password?',
    activationNotice: 'Registration received. You can sign in only after an administrator activates your account.',
    loginFailed: 'Login failed',
    loginUnexpected: 'An error occurred during login',
    webAccessDenied: 'Web access is limited to administrators and configured leaders.',
    chooseInstitution: 'Choose your institution',
    chooseInstitutionCopy: 'Your permissions and data scope will match this institution.',
    mobileOnly: 'Mobile access only',
    webMember: 'Web member',
    opening: 'Opening…',
    active: 'Active',
    signInAnother: 'Sign in with another account',
    institutionFailed: 'Institution selection failed',
    allSystems: 'ALL SYSTEMS OPERATIONAL · LIVE SOS APP LINK',
  },
  ar: {
    dir: 'rtl',
    languageButton: 'English',
    languageButtonLabel: 'Switch to English',
    brandLines: ['نظام إدارة', 'الأزمات الذكي'],
    headline: ['جاهزون قبل الحدث.', 'مسيطرون أثناء الحدث.'],
    phases: [
      { key: 'prevention', color: '#28ADE3', text: 'الوقاية — نرى الخطر قبل أن يحدث' },
      { key: 'preparedness', color: '#FFC000', text: 'الاستعداد — خطط وجاهزية وأشخاص' },
      { key: 'response', color: '#EF3937', text: 'الاستجابة — تنسيق لحظي في الميدان' },
      { key: 'recovery', color: '#3CB371', text: 'التعافي — نستعيد ونتعلّم' },
    ],
    trust: [
      { value: '24/7', label: 'مركز قيادة' },
      { value: '< 60s', label: 'من الإنذار إلى الإرسال' },
      { value: 'لحظي', label: 'تفعيل استجابة متكاملة' },
    ],
    loginTitle: 'تسجيل الدخول إلى DisasterMS',
    noAccount: 'ليس لديك حساب؟',
    register: 'سجّل الآن',
    emailLabel: 'البريد الإلكتروني',
    emailPlaceholder: 'أدخل بريدك الإلكتروني',
    passwordLabel: 'كلمة المرور',
    passwordPlaceholder: 'أدخل كلمة المرور',
    showPassword: 'إظهار كلمة المرور',
    hidePassword: 'إخفاء كلمة المرور',
    accessConsole: 'دخول وحدة التحكم',
    signingIn: 'جارٍ تسجيل الدخول…',
    forgotPassword: 'نسيت كلمة المرور؟',
    activationNotice: 'تم استلام التسجيل. يمكنك تسجيل الدخول بعد تفعيل الحساب من المسؤول.',
    loginFailed: 'تعذر تسجيل الدخول',
    loginUnexpected: 'حدث خطأ أثناء تسجيل الدخول',
    webAccessDenied: 'الدخول عبر الويب متاح للمسؤولين والقادة المصرّح لهم فقط.',
    chooseInstitution: 'اختر المؤسسة',
    chooseInstitutionCopy: 'ستتطابق صلاحياتك ونطاق بياناتك مع المؤسسة المختارة.',
    mobileOnly: 'دخول عبر الجوال فقط',
    webMember: 'عضو ويب',
    opening: 'جارٍ الفتح…',
    active: 'نشط',
    signInAnother: 'تسجيل الدخول بحساب آخر',
    institutionFailed: 'تعذر اختيار المؤسسة',
    allSystems: 'جميع الأنظمة تعمل · اتصال لحظي بتطبيق SOS',
  },
};

const UTC_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [institutionSelection, setInstitutionSelection] = useState(null);
  const [selectingInstitutionId, setSelectingInstitutionId] = useState('');
  const [showActivationNotice, setShowActivationNotice] = useState(false);
  const [language, setLanguage] = useState(readAuthLanguage);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [utcTime, setUtcTime] = useState(() => UTC_FORMATTER.format(new Date()));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const text = PAGE_COPY[language];
  const currentPhase = text.phases[phaseIndex];

  useEffect(() => {
    // The public page has committed, so a future authenticated session must
    // no longer inherit the one-render logout transition guard.
    finishLogoutTransition();
    resetMarketplaceGuideForNextLogin();
    // Successful logout intentionally keeps its modal mounted until this
    // public route commits. Reset the Redux flag here, after the protected
    // shell has been replaced, so no stale protected page can flash.
    dispatch(logoutModalToggle(false));
  }, [dispatch]);

  useEffect(() => {
    if (sessionStorage.getItem('showLoginActivationNotice') === '1') {
      setShowActivationNotice(true);
      sessionStorage.removeItem('showLoginActivationNotice');
    }
  }, []);

  useEffect(() => {
    const phaseTimer = window.setInterval(() => {
      setPhaseIndex((current) => (current + 1) % PAGE_COPY.en.phases.length);
    }, 3200);
    return () => window.clearInterval(phaseTimer);
  }, []);

  useEffect(() => {
    const updateClock = () => setUtcTime(UTC_FORMATTER.format(new Date()));
    updateClock();
    const clockTimer = window.setInterval(updateClock, 1000);
    return () => window.clearInterval(clockTimer);
  }, []);

  const completeAuthentication = (result, institution = null) => {
    if (!result || result.type !== 'authenticated') return false;
    if (!hasWebAccess(result.user)) {
      clearWebSession();
      dispatch(disconnectSocket());
      setError(`${text.webAccessDenied} ${MOBILE_APP_ONLY_MESSAGE}`);
      return false;
    }

    persistAuthenticationResult(result, institution);
    CommanServices.setToken(result.accessToken);
    dispatch(connectSocket(result.accessToken));
    navigate(loginDestinationForUser(result.user));
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await CommanServices.login({ email: email.trim(), password });
      const result = extractAuthenticationResult(response);
      if (result?.type === 'institution_selection') {
        setInstitutionSelection(result);
        return;
      }
      if (!completeAuthentication(result)) {
        setError((current) => current || apiErrorMessage(response, text.loginFailed));
      }
    } catch (err) {
      setError(err?.message || text.loginUnexpected);
    } finally {
      setLoading(false);
    }
  };

  const handleInstitutionSelection = async (institution) => {
    if (!institutionSelection?.selectionToken || !institution?.id) return;
    setError('');
    setSelectingInstitutionId(institution.id);
    try {
      const response = await SaaSServices.selectInstitution(
        institutionSelection.selectionToken,
        institution.id,
      );
      const result = extractAuthenticationResult(response);
      if (!completeAuthentication(result, institution)) {
        setError((current) => current || apiErrorMessage(response, text.institutionFailed));
      }
    } catch (err) {
      setError(err?.message || text.institutionFailed);
    } finally {
      setSelectingInstitutionId('');
    }
  };

  const toggleLanguage = () => {
    const nextLanguage = language === 'en' ? 'ar' : 'en';
    persistAuthLanguage(nextLanguage);
    setLanguage(nextLanguage);
    setError('');
  };

  return (
    <div
      className={`dms-login-page${forgotPasswordMode ? ' dms-login-page--forgot' : ''}`}
      dir={text.dir}
      lang={language}
      data-testid="disasterms-login-page"
    >
      <div className="dms-login-top-line" aria-hidden="true" />
      <div className="dms-login-glow" aria-hidden="true" />
      <div className="dms-login-decor" aria-hidden="true">
        <img
          className="dms-login-watermark"
          src={brandWatermark}
          alt=""
          draggable="false"
        />
      </div>

      <button
        type="button"
        className="dms-language-toggle"
        onClick={toggleLanguage}
        aria-label={text.languageButtonLabel}
      >
        <Globe2 size={15} aria-hidden="true" />
        <span>{text.languageButton}</span>
      </button>

      {institutionSelection && (
        <div className="dms-institution-overlay">
          <section
            className="dms-institution-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="institution-selection-title"
          >
            <header className="dms-institution-dialog-header">
              <span className="dms-institution-dialog-icon">
                <Building2 size={21} aria-hidden="true" />
              </span>
              <div>
                <h2 id="institution-selection-title" className="dms-institution-dialog-title">
                  {text.chooseInstitution}
                </h2>
                <p className="dms-institution-dialog-copy">{text.chooseInstitutionCopy}</p>
              </div>
            </header>

            <div className="dms-institution-list">
              {institutionSelection.institutions.map((institution) => {
                const webEnabled = institution.web_enabled === true;
                const busy = selectingInstitutionId === institution.id;
                const roleLabel = webEnabled
                  ? (institution.institution_roles || []).join(' · ') || text.webMember
                  : text.mobileOnly;
                return (
                  <button
                    key={institution.id}
                    type="button"
                    onClick={() => handleInstitutionSelection(institution)}
                    disabled={!webEnabled || Boolean(selectingInstitutionId)}
                    className="dms-institution-option"
                    data-testid={`institution-option-${institution.id}`}
                  >
                    <span>
                      <span className="dms-institution-name">
                        {institution.display_name || institution.slug || institution.id}
                      </span>
                      <span className="dms-institution-role">{roleLabel}</span>
                    </span>
                    <span className="dms-institution-status">
                      {busy ? text.opening : institution.status || text.active}
                    </span>
                  </button>
                );
              })}
              {error && (
                <p className="dms-auth-alert dms-auth-alert--error" role="alert">
                  {error}
                </p>
              )}
            </div>

            <footer className="dms-institution-dialog-footer">
              <button
                type="button"
                className="dms-auth-back"
                onClick={() => {
                  setInstitutionSelection(null);
                  setSelectingInstitutionId('');
                  setError('');
                }}
              >
                <ArrowLeft size={16} aria-hidden="true" />
                <span>{text.signInAnother}</span>
              </button>
            </footer>
          </section>
        </div>
      )}

      <main className="dms-login-main">
        <section className="dms-brand-story" aria-labelledby="disasterms-login-headline">
          <div className="dms-brand-lockup">
            <img
              className="dms-brand-mark"
              src={brandMark}
              alt=""
              draggable="false"
            />
            <p className="dms-brand-wordmark">
              {text.brandLines.map((line) => (
                <React.Fragment key={line}>
                  {line}
                  <br />
                </React.Fragment>
              ))}
            </p>
          </div>

          <h1 id="disasterms-login-headline" className="dms-brand-headline">
            {text.headline.map((line) => <span key={line}>{line}</span>)}
          </h1>

          <div className="dms-phase-line" aria-live="polite">
            <span className="dms-phase-dot" style={{ color: currentPhase.color }} aria-hidden="true" />
            <span className="dms-phase-copy" key={`${language}-${currentPhase.key}`}>
              {currentPhase.text}
            </span>
          </div>

          <div className="dms-trust-strip" aria-label="Platform readiness indicators">
            {text.trust.map((item) => (
              <div className="dms-trust-item" key={item.label}>
                <span className="dms-trust-value">{item.value}</span>
                <span className="dms-trust-label">{item.label}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="dms-auth-card" aria-label={forgotPasswordMode ? undefined : text.loginTitle}>
          {forgotPasswordMode ? (
            <ForgotPasswordModal
              error={error}
              setError={setError}
              loading={loading}
              setLoading={setLoading}
              setForgotPasswordMode={setForgotPasswordMode}
              language={language}
            />
          ) : (
            <>
              <h2 className="dms-auth-card-title">{text.loginTitle}</h2>
              <p className="dms-auth-card-copy">
                {text.noAccount}{' '}
                <Link to="/signup">{text.register}</Link>
              </p>

              {showActivationNotice && (
                <p className="dms-auth-alert dms-auth-alert--notice" role="status">
                  {text.activationNotice}
                </p>
              )}

              <form className="dms-auth-form" onSubmit={handleSubmit}>
                <div className="dms-auth-group">
                  <label className="dms-auth-label" htmlFor="login-email">{text.emailLabel}</label>
                  <div className="dms-auth-field">
                    <input
                      id="login-email"
                      name="email"
                      type="email"
                      dir="ltr"
                      autoComplete="email"
                      placeholder={text.emailPlaceholder}
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      className="dms-auth-input"
                      required
                    />
                  </div>
                </div>

                <div className="dms-auth-group">
                  <label className="dms-auth-label" htmlFor="login-password">{text.passwordLabel}</label>
                  <div className="dms-auth-field">
                    <input
                      id="login-password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder={text.passwordPlaceholder}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      className="dms-auth-input"
                      required
                    />
                    <button
                      type="button"
                      className="dms-auth-icon-button"
                      onClick={() => setShowPassword((current) => !current)}
                      aria-label={showPassword ? text.hidePassword : text.showPassword}
                      aria-pressed={showPassword}
                    >
                      {showPassword ? <EyeOff size={17} aria-hidden="true" /> : <Eye size={17} aria-hidden="true" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <p className="dms-auth-alert dms-auth-alert--error" role="alert">
                    {error}
                  </p>
                )}

                <button className="dms-auth-submit" disabled={loading} type="submit">
                  {loading ? text.signingIn : text.accessConsole}
                </button>
              </form>

              <div className="dms-auth-meta">
                <button
                  onClick={() => {
                    setForgotPasswordMode(true);
                    setError('');
                  }}
                  className="dms-auth-link"
                  type="button"
                >
                  {text.forgotPassword}
                </button>
              </div>
            </>
          )}
        </section>
      </main>

      <footer className="dms-login-footer">
        <span className="dms-login-footer-status">
          <span className="dms-login-footer-dot" aria-hidden="true" />
          <span>{text.allSystems}</span>
        </span>
        <span className="dms-login-footer-spacer" aria-hidden="true" />
        <time className="dms-login-clock" dateTime={`${new Date().toISOString().slice(0, 10)}T${utcTime}Z`}>
          {utcTime} UTC
        </time>
        <span className="dms-login-footer-copyright">© 2026 DisasterMS/SOS Ecosystem</span>
      </footer>
    </div>
  );
};

export default Login;
